import { useCallback, useRef } from 'react';

import { useAppNavigate } from '@/app/router/useAppNavigate';
import { tokenStorage } from '@/services/tokenStorage';
import { api } from '@/store/api/api';
import { useAppDispatch } from '@/store/hooks';
import { resetFilters } from '@/store/slices/filtersSlice';
import { setProfilePhoto } from '@/store/slices/uiSlice';

import { authApi } from './auth.api';

/**
 * Sign out of the live dashboard:
 * 1. revoke the session server-side — best effort: an offline or failing API
 *    must never keep someone signed in on this device;
 * 2. leave the live tree, then forget the tokens;
 * 3. drop every cached response, the live chips (customer data) and a photo
 *    saved this session.
 *
 * Leaving before clearing the tokens keeps RequireAuth from remembering the
 * page as a "return to after sign-in" target, and nothing refetches against
 * a reset cache while the tree is still mounted.
 */
export function useSignOut() {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const [logout, { isLoading }] = authApi.useLogoutMutation();
  const inFlight = useRef(false);

  const signOut = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) await logout({ refreshToken });
      await navigate('/login', { replace: true });
      tokenStorage.clear();
      dispatch(api.util.resetApiState());
      dispatch(resetFilters({ source: 'live' }));
      dispatch(setProfilePhoto(undefined));
    } finally {
      inFlight.current = false;
    }
  }, [dispatch, logout, navigate]);

  return { signOut, isSigningOut: isLoading };
}
